import { ReactNode } from 'react';

import { VisitDocumentApproveCommand, VisitDocumentApproveResult } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { ActionEventInterceptor } from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';
import { Choice } from '@dreryk/watermelon';

import {
   DocumentTemplateFormBean,
   DocumentTemplateFormController,
   documentTemplateValuesMapToVisitDocumentFieldValue,
} from '~libs.desktop.shared.document-template';
import { useMedicalDocumentSigningStatusHandler } from '~libs.desktop.shared.medical-documentation';

import { getDocumentTemplateDraftToken } from '../../documentTemplate.draft';
import { translationKeys } from '../../translations';

import { VisitApproveOnSuccessCallback } from './VisitDocumentApproveActionInterceptor.types';
import { getDocumentTemplateToken } from './VisitDocumentApproveActionInterceptor.utils';

type VisitDocumentApproveActionInterceptorProps = {
   readonly patientId: number;
   readonly children: ReactNode;
   readonly visitId: number;
   readonly printTemplateId: number;
   readonly onSuccess?: VisitApproveOnSuccessCallback;
} & Pick<DocumentTemplateFormController, 'handleSubmit' | 'markPristine'>;

export const VisitDocumentApproveActionInterceptor = (
   props: VisitDocumentApproveActionInterceptorProps
): JSX.Element => {
   const handleDocumentSigningStatus = useMedicalDocumentSigningStatusHandler();
   const { executeAsync: removeDraft } = useDraftRemoveByTokenCqrsCommand();

   const { executeAsync: approveVisitDocument } = useCqrsCommand<VisitDocumentApproveResult, DocumentTemplateFormBean>(
      (parameters) => {
         const command = new VisitDocumentApproveCommand();
         command.fieldValues = documentTemplateValuesMapToVisitDocumentFieldValue(parameters.documentTemplateValues);
         command.token = Choice.of(getDocumentTemplateToken(props.patientId, props.printTemplateId));
         command.printTemplateId = props.printTemplateId;
         command.visitId = props.visitId;
         return command;
      },
      {
         onSuccess: async (result) => {
            const { signingStatus } = result;
            props.markPristine();
            await props.onSuccess?.(result);
            handleDocumentSigningStatus(signingStatus);
            await removeDraft({ token: getDocumentTemplateDraftToken(props.visitId, props.printTemplateId) });
         },
      }
   );

   return (
      <ActionEventInterceptor
         action={props.handleSubmit(approveVisitDocument)}
         blockingOperation={translationKeys.confirmingDocument}
      >
         {props.children}
      </ActionEventInterceptor>
   );
};
